import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from "@angular/core";
import { BaseComponent } from "ng/src/app/shared/base.component";
import { BaseEntity, ComplexObjectVersion } from "app/shared/model/app.model";
import { BehaviorSubject, forkJoin, Observable, Subscription, throwError } from "rxjs";
import { Nullable, Optional } from "ng/src/app/shared/model/app.model";
import { catchError, filter, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { GeneralParameters } from "app/shared/model/request-parameter.model";
import { SelectorItem } from "ng/src/app/shared/item-selectors/item-selector.model";
import { RollbackValueAlias } from "ng/src/app/core/value";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { ConfirmationService } from "primeng/api";
import { InplaceEditorComponent } from "ng/src/app/shared/inplace-editor/inplace-editor.component";

@Component({
	template: ''
})
export abstract class ComplexEntityManagerComponent<Type extends ComplexObjectVersion & BaseEntity> extends BaseComponent implements OnInit {
	@ViewChild('inplaceEditor') editor!: InplaceEditorComponent;
	
	@Input()
	set baseObject(o: Type) {
		this.baseObjectSubject.next(o);
	}
	
	@Input() noSelectionText = 'Select an item to continue';
	@Input() noCommentMessage = `${AppStateService.screenAction} to add a comment`;
	@Input() publishEnabled = true
	@Input() editEnabled = true;
	@Input() deleteEnabled = true;
	
	@Output() onPublish = new EventEmitter<Type>();
	@Output() onEdit = new EventEmitter<Type>();
	@Output() onDelete = new EventEmitter<Type>();
	
	history: SelectorItem<Type>[] = [];
	published?: Type;
	draft?: Type;
	
	allItems: SelectorItem<Type>[] = [];
	
	currentComment?: RollbackValueAlias<string>;
	
	get selected() {
		return this._selected;
	}
	
	set selected(val) {
		if (val === this._selected) return;
		this._selected = val;
		this.currentComment?.rollback();
		this.editor?.close('reject');
		this.currentComment = val ? this.createCommentValueAlias(val) : undefined;
	}
	
	get confirmationService() {
		return this.injector.get(ConfirmationService);
	}
	
	
	protected baseObjectSubject = new BehaviorSubject(Nullable.of<Type>());
	private _selected?: Type;
	
	protected constructor(
		injector: Injector
	) {
		super(injector);
	}
	
	ngOnInit(): void {
		this.baseObjectSubject.pipe(
			takeUntil(this.onDestroy$),
			filter(cpxVer => cpxVer != null),
			switchMap((cpxVer) => this.refresh(cpxVer!, false))
		)
			.subscribe();
	}
	
	getDraft(version: Type): Observable<Optional<Type>> {
		return this.performQuery({
			size: 1,
			isDraft: true,
			saveInProgress: false,
			'complexObject.id': version.complexObject.id
		})
			.pipe(map(res => res[0]));
	}
	
	getPublished(version: Type): Observable<Optional<Type>> {
		return this.performQuery({
			size: 1,
			isDraft: false,
			activeRevision: true,
			saveInProgress: false,
			'complexObject.id': version.complexObject.id
		})
			.pipe(map(res => res[0]));
	}
	
	getHistory(version: Type) {
		return this.performQuery({
			sort: 'published,desc',
			isDraft: false,
			activeRevision: false,
			saveInProgress: false,
			'complexObject.id': version.complexObject.id
		})
	}
	
	isDraft(item: SelectorItem<Type>) {
		return this.draft && item.value === this.draft;
	}
	
	isPublished(item: SelectorItem<Type>) {
		return this.published && item.value === this.published;
	}
	
	refresh(obj: Type, subscribe: false): Observable<{history: Type[], published: Optional<Type>, draft: Optional<Type>}>;
	refresh(obj: Type, subscribe: true): Subscription;
	refresh(obj: Type, subscribe = false) {
		const ob$ = forkJoin({
			history: this.getHistory(obj),
			published: this.getPublished(obj),
			draft: this.getDraft(obj)
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap((results) => {
					this.history = results.history.map(value => this.format(value));
					this.draft = results.draft;
					this.published = results.published;
					
					this.allItems = [];
					if (this.draft) {
						this.allItems.push(this.format(this.draft));
					}
					if (this.published) {
						this.allItems.push(this.format(this.published));
					}
					this.allItems = [...this.allItems, ...this.history];
					this.selected = this.published ? this.published : this.draft;
				})
			);
		
		return subscribe ? ob$.subscribe() : ob$;
	}
	
	edit(obj: Type) {
		this.onEdit.emit(obj);
	}
	
	publish(obj: Type, event: Event, confirmed = false) {
		if (!confirmed) {
			this.confirmationService.confirm({
				key: this.key,
				target: event.target ?? undefined,
				message: `Are you sure you want to publish this revision of ${this.getName(obj)}?`,
				accept: () => this.publish(obj, event, true),
				acceptLabel: 'Confirm',
				acceptButtonStyleClass: 'p-button-success',
				rejectButtonStyleClass: 'p-button-secondary',
				defaultFocus: 'reject'
			});
			
			return;
		}
		
		return this.performPatch(obj, {
			publish: true
		})
			.pipe(
				takeUntil(this.onDestroy$),
				switchMap(() => this.refresh(obj, false))
			)
			.subscribe(() => {
				this.onPublish.emit(obj);
			});
	}
	
	saveComment() {
		const selected = this.selected!;
		const rollback = this.currentComment!;
		
		return this.performPatch(selected, {
			versionNote: selected.versionNote
		})
			.pipe(
				takeUntil(this.onDestroy$),
				catchError(err => {
					rollback.rollback();
					return throwError(err);
				})
			)
			.subscribe();
	}
	
	delete(obj: Type, event: Event, confirmed = false) {
		if (!confirmed) {
			this.confirmationService.confirm({
				key: this.key,
				target: event.target ?? undefined,
				message: `Are you sure you want to delete this revision of ${this.getName(obj)}?`,
				accept: () => this.delete(obj, event, true),
				acceptLabel: 'Confirm',
				acceptButtonStyleClass: 'p-button-danger',
				rejectButtonStyleClass: 'p-button-secondary',
				defaultFocus: 'reject'
			});
			
			return;
		}
		
		
		const wasDraft = obj === this.draft;
		
		return this.performDelete(obj).pipe(
			takeUntil(this.onDestroy$)
		)
			.subscribe(() => {
				this.allItems = this.allItems.filter(i => i.value !== obj);
				
				if (wasDraft)
					delete this.draft;
				
				this.onDelete.emit(obj);
			})
	}
	
	
	getCreatedByName(obj: Type) {
		return `${obj.createdBy.firstName} ${obj.createdBy.lastName}`;
	}
	
	createCommentValueAlias(obj: Type) {
		return new RollbackValueAlias<string>(
			() => obj.versionNote!,
			val => obj.versionNote = val
		);
	}
	
	private format(value: Type): SelectorItem<Type> {
		return SelectorItem.with(value, value.published ? this.formatDate(value.published) : '');
	}
	
	abstract getName(obj: Type): string;
	protected abstract performQuery(query: GeneralParameters): Observable<Type[]>
	protected abstract performDelete(obj: Type): Observable<any>;
	protected abstract performPatch(obj: Type, update: any): Observable<any>;
}